import {
  Clock,
  MapPin,
  Video,
  ExternalLink,
  ChevronDown,
  Link,
  Mail,
  Sparkles,
  CheckSquare,
  Repeat,
} from 'lucide-react';
import { Badge, TaskEventDateIcon } from '@/components/ui';
import { ProfileBadge } from '@/components/ui/ProfileBadge';
import { AvatarGroup } from '@/components/ui/AvatarGroup';
import {
  calendarDay,
  formatCompactDateRange,
  formatDateRange,
  formatTimeRange,
  isAllDaySpan,
  spansMultipleDays,
} from '@/lib/date';

export interface ChildProfile {
  name: string;
  emoji?: string;
  color?: string;
  avatarSrc?: string;
  imagePath?: string | null;
}

/**
 * Household adult (member) assigned to the event. Rendered as the
 * bottom-right overlapping avatar cluster — distinct from `children`,
 * which render as the labelled chip row.
 */
export interface MemberProfile {
  name: string;
  emoji?: string;
  color?: string;
  imagePath?: string | null;
}

export interface EventCardProps {
  id: string;
  title: string;
  description?: string;
  startDate: string;
  endDate?: string;
  location?: string;
  link?: string;
  children?: ChildProfile[];
  /** Household adults assigned to the event (the avatar cluster). */
  members?: MemberProfile[];
  /**
   * Subject of the originating email when the event was committed from
   * the review pipeline. Rendered as a compact "From: …" line.
   */
  sourceSubject?: string;
  category?: string;
  relatedTaskCount?: number;
  onViewRelatedTasks?: () => void;
  /** Tapping the card body (outside join/link/related-tasks) opens edit. */
  onClick?: () => void;
  /**
   * IANA timezone (e.g. "America/Mexico_City") to render the event's
   * time and date-preview block in. Defaults to the browser's timezone
   * when omitted.
   */
  timezone?: string | null;
  /**
   * Visual variant. `"review"` swaps the date-badge left-rail for a small
   * calendar icon with a sparkles overlay to signal AI-extracted items
   * inside the review flow.
   */
  variant?: 'default' | 'review' | 'history';
  /**
   * The source email never stated an hour, so the stored start is a local-noon
   * day-boundary guard rather than a real clock reading. Suppresses the time
   * row — printing it invents an hour the sender never wrote (YAR-376 / 378).
   * Committed events carry this on their `source_review_item` join; it is not
   * a column on `events`.
   */
  isTimeUnknown?: boolean;
  /** Dim the card — used for past events in the History screen. */
  muted?: boolean;
  /** Renders a "{done}/{total} Sub-tasks" footer when provided. */
  subtaskSummary?: { done: number; total: number };
  /**
   * One occurrence of a recurring series (`events.series_id`, YAR-443): the
   * label from `seriesLabel()` — "Repeats weekly", "Repeats every other
   * week", "Repeats every Tuesday". Text, never colour alone. Omit for a
   * one-off event.
   */
  repeatLabel?: string;
}

const MONTH_ABBR = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

export function EventCard({
  title,
  description,
  startDate,
  endDate,
  location,
  link,
  children = [],
  members = [],
  sourceSubject,
  category,
  relatedTaskCount,
  onViewRelatedTasks,
  onClick,
  timezone,
  variant = 'default',
  isTimeUnknown = false,
  muted = false,
  subtaskSummary,
  repeatLabel,
}: EventCardProps) {
  const isHistory = variant === 'history';
  // History files a span under the day it ENDED, so its badge names that day
  // (YAR-176 AC 12). Everywhere else the badge stays on the start: Review
  // previews an event that has not happened yet, where anchoring to the end
  // would name a day the parent has no reason to act on.
  const badgeSource = isHistory ? (endDate ?? startDate) : startDate;
  const date = calendarDay(badgeSource, timezone);
  const month = MONTH_ABBR[date.getMonth()];
  const day = date.getDate();
  // YAR-391: the left-rail badge carries only the START day, so an event
  // ending on a later one previewed as a one-day event — a four-month
  // tuition-liability window was indistinguishable from an ordinary Aug 17
  // all-day event. Rendered ONLY for genuine spans: on a single-day card the
  // badge already is the date, and a second row would print it twice.
  const spansDays = spansMultipleDays(startDate, endDate, timezone);
  const allDay = isAllDaySpan(startDate, endDate, timezone);
  const isClickable = !!onClick;
  const isReview = variant === 'review';

  return (
    <div
      className={[
        // Padding lives on the inner content wrapper, not here, so the
        // sub-tasks band below can run edge to edge (YAR-297). `overflow-hidden`
        // clips that band's corners to the card radius.
        'bg-card border border-border-strong rounded-lg overflow-hidden',
        muted ? 'opacity-60' : '',
        isClickable ? 'cursor-pointer' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="flex items-start gap-4 p-5">
        {isReview ? (
          <div className="relative shrink-0">
            <div className="w-10 h-10 rounded-lg bg-info-bg border border-info-border flex items-center justify-center">
              <TaskEventDateIcon
                size={18}
                className="text-primary"
              />
            </div>
            <Sparkles
              size={14}
              className="absolute -top-1.5 -left-1.5 text-foreground"
              aria-label="AI-extracted"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center bg-info-bg rounded-lg px-3 py-2 shrink-0">
            {/* 14 / 32px per Figma — YAR-257. Was 10 / 20px, which made the
                date badge read as a caption instead of the card's anchor. */}
            <span className="text-sm font-semibold uppercase text-muted-foreground">
              {month}
            </span>
            <span className="text-[32px] leading-10 font-bold text-foreground">
              {day}
            </span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-text-body">{title}</p>

          {description && (
            <p className="text-sm text-muted-foreground mt-1 break-words">
              {description}
            </p>
          )}

          <div className="flex flex-col gap-1 mt-2">
            {spansDays && (
              <div className="flex items-center gap-1.5">
                <TaskEventDateIcon
                  size={14}
                  className="text-muted-foreground"
                />
                <span className="text-sm text-muted-foreground">
                  {isHistory
                    ? formatCompactDateRange(startDate, endDate, timezone)
                    : formatDateRange(startDate, endDate, timezone)}
                </span>
              </div>
            )}
            {/* A multi-day event carries no time, anywhere (YAR-420). The
                stored clock on a span is a day-boundary guard, not a reading
                anyone wrote, so printing it — or relabelling a widened span
                "All day" — asserts a duration the parent never stated. */}
            {!isTimeUnknown && !spansDays && (
              <div className="flex items-center gap-1.5">
                <Clock
                  size={14}
                  className="text-muted-foreground"
                />
                <span className="text-sm text-muted-foreground">
                  {/* A span running 00:00 → 23:59 renders as "All day": the
                      literal "12:00 AM - 11:59 PM" is true but tells a parent
                      nothing. Keyed on the times BEING a full day, never on a
                      time being absent — that case is `isTimeUnknown`. */}
                  {allDay
                    ? 'All day'
                    : formatTimeRange(startDate, endDate, timezone)}
                </span>
              </div>
            )}
            {location && /^https?:\/\//.test(location) && (
              <a
                href={location}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-hover"
              >
                <Video size={14} />
                Join meeting
                <ExternalLink size={12} />
              </a>
            )}
            {location && !/^https?:\/\//.test(location) && (
              <div className="flex items-center gap-1.5">
                <MapPin
                  size={14}
                  className="text-muted-foreground"
                />
                <span className="text-sm text-muted-foreground">
                  {location}
                </span>
              </div>
            )}
            {link && (
              <div className="flex items-center gap-1.5">
                <Link
                  size={14}
                  className="text-muted-foreground"
                />
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 min-w-0 text-sm text-primary hover:text-primary-hover underline-offset-2 hover:underline truncate"
                >
                  <span className="truncate">{link}</span>
                  <ExternalLink
                    size={12}
                    className="shrink-0"
                  />
                </a>
              </div>
            )}
            {repeatLabel && (
              <div className="flex items-center gap-1.5">
                <Repeat
                  size={14}
                  className="text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="text-sm text-muted-foreground">
                  {repeatLabel}
                </span>
              </div>
            )}
          </div>

          {sourceSubject && (
            <div className="flex items-center gap-1.5 mt-2 min-w-0">
              <Mail
                size={14}
                className="text-muted-foreground shrink-0"
              />
              <span className="text-xs text-muted-foreground truncate">
                From: {sourceSubject}
              </span>
            </div>
          )}

          {(children.length > 0 || members.length > 0 || category) && (
            <div className="flex items-center justify-between gap-2 mt-3">
              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                {children.slice(0, 2).map((child) => (
                  <ProfileBadge
                    key={child.name}
                    name={child.name}
                    emoji={child.emoji}
                    color={child.color}
                    imagePath={child.imagePath}
                  />
                ))}
                {children.length > 2 && (
                  <span className="inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                    +{children.length - 2}
                  </span>
                )}
                {category && (
                  <Badge
                    variant="info"
                    size="sm"
                  >
                    {category}
                  </Badge>
                )}
              </div>
              {members.length > 0 && (
                <AvatarGroup
                  className="shrink-0"
                  avatars={members.map((m) => ({
                    emoji: m.emoji,
                    color: m.color,
                    imagePath: m.imagePath,
                    alt: m.name,
                  }))}
                  max={3}
                  size="sm"
                />
              )}
            </div>
          )}

          {relatedTaskCount !== undefined && relatedTaskCount > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onViewRelatedTasks?.();
              }}
              className="flex items-center gap-1 mt-3 pt-3 border-t border-border text-sm font-medium text-muted-foreground hover:text-foreground cursor-pointer bg-transparent border-l-0 border-r-0 border-b-0 px-0"
            >
              <ChevronDown size={14} />
              {relatedTaskCount} Task{relatedTaskCount > 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>
      {/* Sub-tasks band (YAR-297). A full-bleed footer, not a rule inside the
          padding: Figma 7928:64956 gives it `surface/secondary` (#f9f9f9)
          behind a `border/neutral/default-subtle` (#e5e5e5) hairline, running
          the full card width. It renders whenever the event has sub-tasks —
          `0/1` included, per the design. */}
      {subtaskSummary && (
        <div className="flex items-center gap-1.5 px-5 py-3 bg-surface-secondary border-t border-border text-sm font-medium text-muted-foreground">
          <CheckSquare size={14} />
          {subtaskSummary.done}/{subtaskSummary.total} Sub-tasks
        </div>
      )}
    </div>
  );
}
